/** Wizard steps for the phone login modal. */
export const AUTH_STEP = {
  PHONE: 'phone',
  OTP: 'otp',
  SOCIAL: 'social',
  PROFILE: 'profile',
};

/** Stable codes for validation and API failures. */
export const AUTH_ERROR_CODE = {
  INVALID_PHONE: 'INVALID_PHONE',
  INVALID_OTP: 'INVALID_OTP',
  OTP_EXPIRED: 'OTP_EXPIRED',
  PROFILE_INCOMPLETE: 'PROFILE_INCOMPLETE',
  TOO_MANY_ATTEMPTS: 'TOO_MANY_ATTEMPTS',
  UNAUTHORIZED: 'UNAUTHORIZED',
  REQUEST_FAILED: 'REQUEST_FAILED',
  PHONE_HAS_EMAIL: 'PHONE_HAS_EMAIL',
  TICKET_EXPIRED: 'TICKET_EXPIRED',
  ACCOUNT_CONFLICT: 'ACCOUNT_CONFLICT',
  ACCOUNT_MERGE_BLOCKED: 'ACCOUNT_MERGE_BLOCKED',
  WHATSAPP_OTP_DISABLED: 'WHATSAPP_OTP_DISABLED',
  WHATSAPP_SEND_FAILED: 'WHATSAPP_SEND_FAILED',
  UNSUPPORTED_COUNTRY: 'UNSUPPORTED_COUNTRY',
  EMAIL_TAKEN: 'EMAIL_TAKEN',
  EMAIL_NOT_ALLOWED: 'EMAIL_NOT_ALLOWED',
  PHONE_REQUIRED: 'PHONE_REQUIRED',
  SAME_PHONE: 'SAME_PHONE',
  PHONE_TAKEN: 'PHONE_TAKEN',
  PHONE_CHANGE_LIMIT: 'PHONE_CHANGE_LIMIT',
};

export const AUTH_ERROR_MESSAGE = {
  [AUTH_ERROR_CODE.INVALID_PHONE]: 'Enter a valid 10-digit mobile number',
  [AUTH_ERROR_CODE.INVALID_OTP]: 'Enter the 6-digit code',
  [AUTH_ERROR_CODE.OTP_EXPIRED]: 'That code has expired. Request a new one.',
  [AUTH_ERROR_CODE.PROFILE_INCOMPLETE]: 'Enter your first name',
  [AUTH_ERROR_CODE.TOO_MANY_ATTEMPTS]: 'Too many attempts. Try again later.',
  [AUTH_ERROR_CODE.UNAUTHORIZED]: 'Your session has ended. Sign in again.',
  [AUTH_ERROR_CODE.REQUEST_FAILED]: 'Something went wrong. Try again.',
  [AUTH_ERROR_CODE.PHONE_HAS_EMAIL]: 'An account is already created with this mobile number',
  [AUTH_ERROR_CODE.TICKET_EXPIRED]: 'Your sign-up timed out. Please start again.',
  [AUTH_ERROR_CODE.ACCOUNT_CONFLICT]: 'We could not finish setting up this account. Please sign in again or contact support.',
  [AUTH_ERROR_CODE.ACCOUNT_MERGE_BLOCKED]: 'This number is linked to another account. Contact support.',
  [AUTH_ERROR_CODE.WHATSAPP_OTP_DISABLED]: 'WhatsApp codes are not available right now. Please use SMS.',
  [AUTH_ERROR_CODE.WHATSAPP_SEND_FAILED]: 'We could not send it on WhatsApp. Use the code you already have, or resend by SMS.',
  [AUTH_ERROR_CODE.UNSUPPORTED_COUNTRY]: 'Only Indian (+91) mobile numbers are supported right now',
  [AUTH_ERROR_CODE.EMAIL_TAKEN]: 'This email is already used by another account',
  [AUTH_ERROR_CODE.EMAIL_NOT_ALLOWED]: 'This email cannot be used',
  [AUTH_ERROR_CODE.PHONE_REQUIRED]: 'This email has no mobile number linked. Please add your number and verify it.',
  [AUTH_ERROR_CODE.SAME_PHONE]: 'This is already your mobile number',
  [AUTH_ERROR_CODE.PHONE_TAKEN]: 'This number is linked to another account',
  [AUTH_ERROR_CODE.PHONE_CHANGE_LIMIT]: 'You can change your mobile number 3 times in 30 days. Please try again later.',
};

/** Country picked on the mobile number field until the user changes it. */
export const DEFAULT_COUNTRY = 'IN';

/**
 * Countries the login API can send a code to. Not enforced for now: Get OTP sends
 * the 10 digits in the field whatever country is picked.
 */
export const OTP_SUPPORTED_COUNTRIES = ['IN'];

/**
 * Show the SMS ↔ WhatsApp switch on the code screen. Turn on together with the
 * server's WHATSAPP_LOGIN_OTP_ENABLED; if only this is on, the server refuses and
 * the dialog hides the switch for the rest of the session.
 */
export const WHATSAPP_OTP_ENABLED = process.env.REACT_APP_WHATSAPP_LOGIN_OTP_ENABLED === 'true';

/** Phone-link failures that keep the user on Social Auth to try another number. */
export const SOCIAL_RETRY_ERROR_CODES = [
  AUTH_ERROR_CODE.PHONE_HAS_EMAIL,
  AUTH_ERROR_CODE.ACCOUNT_MERGE_BLOCKED,
  AUTH_ERROR_CODE.ACCOUNT_CONFLICT,
];

/** Digits the login OTP input accepts. */
export const OTP_LENGTH = 6;

/** Seconds before the OTP step can request another code. */
export const OTP_RESEND_SECONDS = 30;

/** Sent with session calls so the API can tell web from the app. */
export const AUTH_CLIENT = 'web';

/** Opts the web into signup and social tickets. Without it the API keeps the mobile-app responses. */
export const AUTH_FLOW = 'ticket';

export const OTP_CHANNEL = {
  SMS: 'sms',
  WHATSAPP: 'whatsapp',
};

/** Same keys the existing session already writes. */
export const AUTH_STORAGE_KEY = {
  ACCESS: 'yovo_auth_token',
  REFRESH: 'yovo_refresh_token',
  PROFILE: 'yovo_auth_profile',
};

/** Auth-only analytics event names. Payloads stay free of phone numbers and codes. */
export const AUTH_EVENT = {
  OTP_SENT: 'auth_otp_sent',
  OTP_VERIFIED: 'auth_otp_verified',
  PROFILE_COMPLETED: 'auth_profile_completed',
  LOGOUT: 'auth_logout',
  LOGIN_FAILED: 'auth_login_failed',
};
